import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useThrottledCallback } from 'use-debounce';
import PropTypes from 'prop-types';

import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

import {
  worklogListItemFixedHeight,
  worklogTimelineHeight,
  cardNameHeight,
  commentMarginHeight,
  commentBlockHeight,
  dividerHeight,
} from './constants';
import ListItem from './ListItem';
import { isDailyFirstItem } from './WorklogItem';
import * as S from './styled';

export const getRowHeight = ({
  worklog,
  shouldShowTimeline,
  shouldDisplayDivider,
  cardNamePlacement,
  commentPlacement,
}) => {
  let rowHeight = worklogListItemFixedHeight;

  if (shouldDisplayDivider) {
    rowHeight += dividerHeight;
  }

  if (shouldShowTimeline) {
    rowHeight += worklogTimelineHeight;
  }

  if (commentPlacement === 'body' && worklog?.comment) {
    rowHeight += commentBlockHeight + commentMarginHeight;
  }

  if (cardNamePlacement === 'body') {
    rowHeight += cardNameHeight;
  }

  if (worklog?.isWorklogGroup) {
    const counterBadgeExtraHeight = 16;
    rowHeight += counterBadgeExtraHeight;
  }

  return rowHeight;
};

const WorklogsList = ({
  resetIndicator,
  worklogs,
  listItems,
  dayPoints,
  weekPoints,
  cards,
  users,
  onlineUsers,
  worklogsCount,
  loadMoreWorklogs,
  infiniteLoaderRef,
  autosizerRef,
  listRef,
  isWorklogsListHidden,
  isShowScreenshotsBtn,
  isShowEditBtn,
  isShowDeleteBtn,
  isShowLabel,
  isLabelExpanded,
  showStartBtn,
  isItemLoaded,
  showWorklogButtonsDefault,
  shouldDisplayDivider,
  shouldDisplayCard,
  onShowScreenshots,
  onEditWorklog,
  onHideOwnLiveWorklog,
  onDeleteWorklog,
  onClickCardname,
  onClickExpandLabel,
  onStartWorklog,
  cardNameTextDecoration,
  cardNamePlacement,
  commentPlacement,
  popoverPlacement,
  isMobile,
  showTimeline,
  weekStartsOn,
  setShowLoadMoreButton,
  height: fixedListHeight,
}) => {
  const disableHeight =
    fixedListHeight !== null && fixedListHeight !== undefined;
  const [labelState] = useState(() => new Map());
  const [expandCommentState] = useState(() => new Map());
  const [expandGroupState] = useState(() => new Map());
  const ownListRef = useRef();
  const resetAfterIndex = useRef(null);

  const reRenderItemsNow = (itemIndex) => {
    if (ownListRef.current) {
      ownListRef.current.resetAfterIndex(itemIndex, true);
    }
  };

  const reRenderItems = useThrottledCallback(() => {
    if (resetAfterIndex.current !== null && ownListRef.current) {
      ownListRef.current.resetAfterIndex(resetAfterIndex.current, true);
    }
    resetAfterIndex.current = null;
  }, 100);

  useEffect(() => {
    resetAfterIndex.current = 0;
    reRenderItems();
    setTimeout(() => {
      if (ownListRef.current) {
        ownListRef.current.resetAfterIndex(0, true);
      }
    }, 300);
  }, [resetIndicator]);

  const onClickExpandWorklogGroup = (id, index) => {
    if (expandGroupState.has(id)) {
      expandGroupState.delete(id);
      ownListRef.current.resetAfterIndex(index, true);
      return false;
    }

    expandGroupState.set(id, null);
    ownListRef.current.resetAfterIndex(index, true);
    return true;
  };

  const isWorklogGroupExpanded = (id) => {
    return expandGroupState.has(id);
  };

  // Handle comment body expansion.
  const onClickExpandComment = (id, index, shouldReset = true) => {
    // If the comment is already opened. Close it and remove state.
    if (expandCommentState.has(id)) {
      expandCommentState.delete(id);
      if (shouldReset) {
        ownListRef.current.resetAfterIndex(index, true);
      }
      return false;
    }

    // Set initial size to null. It will be repaced with numeric value by the first resize event.
    expandCommentState.set(id, null);
    if (shouldReset) {
      ownListRef.current.resetAfterIndex(index, true);
    }
    return true;
  };

  const isCommentExpanded = (id) => {
    return expandCommentState.has(id);
  };

  const onResizeCommentArea = (id, index, size) => {
    if (expandCommentState.has(id)) {
      // Ignore resize event when WorklogItem is out of viewport
      if (size.height !== 0 && expandCommentState.get(id) !== size.height) {
        expandCommentState.set(id, size.height);
        if (
          resetAfterIndex.current === null ||
          resetAfterIndex.current > index
        ) {
          resetAfterIndex.current = index;
        }
        reRenderItems();
      }
    }
  };

  const onResizeLabelArea = (id, index, size) => {
    // Ignore resize event when WorklogItem is out of viewport
    if (size.height !== 0 && labelState.get(id) !== size.height) {
      labelState.set(id, size.height);

      if (resetAfterIndex.current === null || resetAfterIndex.current > index) {
        resetAfterIndex.current = index;
      }
      reRenderItems();
    }
  };

  const onResizeWorglogGroup = (id, index, size) => {
    if (size.height !== 0 && expandGroupState.get(id) !== size.height) {
      expandGroupState.set(id, size.height);

      if (resetAfterIndex.current === null || resetAfterIndex.current > index) {
        resetAfterIndex.current = index;
      }
      reRenderItems();
    }
  };

  const getItemSize = (index) => {
    const shouldShowTimeline =
      showTimeline &&
      isDailyFirstItem({
        index,
        worklogs,
        listItems,
      });
    const worklog = worklogs[listItems[index]] ?? listItems[index];
    let height = getRowHeight({
      worklog,
      shouldShowTimeline,
      shouldDisplayDivider,
      cardNamePlacement,
      commentPlacement,
    });

    if (worklog?.isWorklogGroup && expandGroupState.has(worklog.id)) {
      height += expandGroupState.get(worklog.id);
    }

    const worklogId = worklog?.isLive ? worklog.startTime : worklog?.id;
    if (worklogId && expandCommentState.has(worklogId)) {
      height = height - commentBlockHeight + expandCommentState.get(worklogId);
    }

    if (isShowLabel && worklogId) {
      const labelsLineHeight = isLabelExpanded ? 16 : 8;
      const labelsMarginTop = 5;
      if (labelState.has(worklogId)) {
        height += labelState.get(worklogId) + labelsMarginTop;
      } else if (worklog) {
        const card = cards[worklog.cardId];
        if ((card?.labels ?? []).length) {
          height += labelsLineHeight + labelsMarginTop;
        }
      }
    }

    return height;
  };
  const onItemsRenderedWrap = useCallback(
    (onItemsRendered) => (options) => {
      const { visibleStopIndex } = options;
      if (setShowLoadMoreButton) {
        setShowLoadMoreButton(visibleStopIndex === worklogsCount - 1);
      }
      return onItemsRendered(options);
    },
    [worklogsCount]
  );

  return (
    <S.WorklogsList
      isHidden={isWorklogsListHidden}
      height={fixedListHeight}
    >
      <AutoSizer
        ref={autosizerRef}
        disableHeight={disableHeight}
      >
        {({ height, width }) => {
          return (
            <InfiniteLoader
              ref={infiniteLoaderRef}
              isItemLoaded={isItemLoaded}
              itemCount={worklogsCount}
              loadMoreItems={loadMoreWorklogs}
              minimumBatchSize={10}
            >
              {({ onItemsRendered, ref }) => (
                <S.List
                  height={disableHeight ? fixedListHeight : height}
                  width={width}
                  itemCount={worklogsCount}
                  itemSize={getItemSize}
                  overscanCount={5}
                  onItemsRendered={onItemsRenderedWrap(onItemsRendered)}
                  ref={(el) => {
                    if (listRef) {
                      listRef.current = el;
                    }
                    ownListRef.current = el;
                    ref(el);
                  }}
                  itemData={{
                    worklogs,
                    listItems,
                    dayPoints,
                    weekPoints,
                    isShowScreenshotsBtn,
                    isShowEditBtn,
                    isShowDeleteBtn,
                    showStartBtn,
                    shouldDisplayDivider,
                    showTimeline,
                    isShowLabel,
                    isLabelExpanded,
                    showWorklogButtonsDefault,
                    shouldDisplayCard,
                    cards,
                    users,
                    onlineUsers,
                    isItemLoaded,
                    onShowScreenshots,
                    onEditWorklog,
                    onHideOwnLiveWorklog,
                    onDeleteWorklog,
                    onClickCardname,
                    onClickExpandLabel: () => {
                      onClickExpandLabel();
                      resetAfterIndex.current = 0;
                      reRenderItems();
                    },
                    onClickExpandComment,
                    onClickExpandWorklogGroup,
                    onResizeLabelArea,
                    onResizeWorglogGroup,
                    onResizeCommentArea,
                    onStartWorklog,
                    cardNameTextDecoration,
                    cardNamePlacement,
                    commentPlacement,
                    popoverPlacement,
                    isMobile,
                    isWorklogGroupExpanded,
                    isCommentExpanded,
                    reRenderItemsNow,
                    weekStartsOn,
                  }}
                >
                  {ListItem}
                </S.List>
              )}
            </InfiniteLoader>
          );
        }}
      </AutoSizer>
    </S.WorklogsList>
  );
};

WorklogsList.propTypes = {
  resetIndicator: PropTypes.number,
  worklogs: PropTypes.object.isRequired,
  listItems: PropTypes.object.isRequired,
  dayPoints: PropTypes.object.isRequired,
  weekPoints: PropTypes.object.isRequired,
  cards: PropTypes.object.isRequired,
  users: PropTypes.object.isRequired,
  onlineUsers: PropTypes.object,
  worklogsCount: PropTypes.number.isRequired,
  loadMoreWorklogs: PropTypes.func.isRequired,
  infiniteLoaderRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(InfiniteLoader) }),
  ]).isRequired,
  listRef: PropTypes.shape({
    current: PropTypes.oneOfType([
      PropTypes.oneOf([null]).isRequired,
      PropTypes.instanceOf(VariableSizeList),
    ]),
  }),
  autosizerRef: PropTypes.shape({
    current: PropTypes.oneOfType([
      PropTypes.oneOf([null]).isRequired,
      PropTypes.instanceOf(AutoSizer),
    ]),
  }),
  height: PropTypes.number,
  showTimeline: PropTypes.bool.isRequired,
  showStartBtn: PropTypes.bool.isRequired,
  isWorklogsListHidden: PropTypes.bool.isRequired,
  isShowScreenshotsBtn: PropTypes.bool.isRequired,
  isShowEditBtn: PropTypes.bool.isRequired,
  isShowDeleteBtn: PropTypes.bool.isRequired,
  isShowLabel: PropTypes.bool,
  isLabelExpanded: PropTypes.bool,
  isItemLoaded: PropTypes.func.isRequired,
  showWorklogButtonsDefault: PropTypes.bool.isRequired,
  shouldDisplayCard: PropTypes.bool.isRequired,
  shouldDisplayDivider: PropTypes.bool.isRequired,
  onShowScreenshots: PropTypes.func,
  onEditWorklog: PropTypes.func.isRequired,
  onHideOwnLiveWorklog: PropTypes.func.isRequired,
  onDeleteWorklog: PropTypes.func.isRequired,
  onClickCardname: PropTypes.func,
  onClickExpandLabel: PropTypes.func,
  onStartWorklog: PropTypes.func,
  cardNameTextDecoration: PropTypes.string,
  cardNamePlacement: PropTypes.oneOf(['title', 'body']),
  commentPlacement: PropTypes.oneOf(['title', 'body']),
  popoverPlacement: PropTypes.oneOf([
    'start',
    'end',
    'above',
    'right',
    'below',
    'left',
    'column',
    'row',
  ]),
  isMobile: PropTypes.bool,
  weekStartsOn: PropTypes.number,
  setShowLoadMoreButton: PropTypes.func.isRequired,
};

WorklogsList.defaultProps = {
  cardNameTextDecoration: 'underline',
  cardNamePlacement: 'title',
  commentPlacement: 'body',
  popoverPlacement: 'left',
  onlineUser: {},
  isMobile: false,
  isShowLabel: false,
  isLabelExpanded: false,
  weekStartsOn: 0,
};

export default WorklogsList;
